import { Select } from "@JohnLewisPartnership/jl-design-system/dist/components/select/Select";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { dispatchNewEvent } from "@/utils/analytics/trigger/analyticsTrigger";
import EmailLiveChat from "../email-live-chat/EmailLiveChat";
import styles from "./email-questions.module.scss";
import LiveChatButton from "@/components/live-chat-button/LiveChatButton";
import LoadingSpinner from "@/components/loading-spinner/LoadingSpinner";
import { getSolutionContactDetailsByTag } from "@/utils/data/getContactDetails";
import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { isFeatureCookiePresent, isFeatureCookieSet } from "@/utils/feature-flags";
import { useExperience } from "@/context/MonetateContext";
import { emailTopicsMappingToBreadcrumbsAndSolutionTags } from "./utils";

function EmailQuestions({ onFinished, emailData, isLiveChatInEmailUsEnabled = false, setEmailTopic = () => {} }) {
  const router = useRouter();
  const urlNodes = router.query?.t?.split("-");
  const hide = router.query?.from === "orders";
  const [questions, setQuestions] = useState([{ questionId: "subjects" }]);
  const [formData, setFormData] = useState(null);
  const [liveChatDiscarded, setLiveChatDiscarded] = useState(false);
  const isLiveChatProminenceFeatureFlagSet = useFeatureFlag("live_chat_prominence_experiment_2a");
  const isLiveChatProminenceFeatureCookieSet = isFeatureCookieSet("live_chat_prominence_experiment_2a");
  const isLiveChatProminenceExperiment =
    useExperience("RD | AB-6553 | Customer & Account | Live Chat Prominence Experience - 2A")?.variantLabel ===
    "Experiment";
  const [firstAnswer, setFirstAnswer] = useState(null);
  const [displayLiveChat, setDisplayLiveChat] = useState(false);
  const [isFetchingLiveChatConfig, setIsFetchingLiveChatConfig] = useState(false);
  const [chatConfig, setChatConfig] = useState(null);

  const emailTopicsEligibleForLiveChat = Object.keys(emailTopicsMappingToBreadcrumbsAndSolutionTags);

  // Cookie value takes the highest precedence if present,
  // otherwise both the gitlab feature flag and monetate experiment group need to be true for live chat to be displayed
  const getLiveChatFeatureFlagStatus = () => {
    if (isFeatureCookiePresent("live_chat_prominence_experiment_2a")) {
      return isLiveChatProminenceFeatureCookieSet;
    } else {
      return isLiveChatProminenceFeatureFlagSet && isLiveChatProminenceExperiment;
    }
  };

  const isLiveChatFeatureEnabled = getLiveChatFeatureFlagStatus();

  const buildQuestionsFromUrlNodes = () => {
    const questionsArr = [{ questionId: "subjects" }];
    let tempFormData;
    if (urlNodes) {
      urlNodes.forEach((n, i) => {
        questionsArr[i].answer = n;
        questionsArr.push({ questionId: n });
        // Check if question that contains form data is being loaded
        if (emailData[n]?.form) {
          setFormData(emailData[n].form);
          tempFormData = emailData[n].form;
        }
        // Check if it's last one
        if (!emailData[n]?.select && !emailData[n]?.nextQuestion) {
          onFinished({
            formData: tempFormData,
          });
        }
      });
      setLiveChatDiscarded(true);
    }

    setQuestions(questionsArr);

    // Set first answer to trigger live chat display if eligible right away based on URL params
    if (questionsArr[0].answer) {
      setFirstAnswer(questionsArr[0].answer);
    }
  };

  useEffect(() => {
    buildQuestionsFromUrlNodes();
  }, []);

  useEffect(() => {
    // Set email topic in parent component so that it can relay to the email form component (sibling of this one)
    // and be used for monetate event tracking on form submission as part of the AB6553 experiment
    setEmailTopic(firstAnswer);

    // Check if first answer is eligible for live chat
    // and if the feature is enabled before fetching live chat config and displaying live chat
    const eligibleForLiveChat = emailTopicsEligibleForLiveChat.includes(firstAnswer) && isLiveChatFeatureEnabled;

    if (eligibleForLiveChat) {
      setIsFetchingLiveChatConfig(true); // Set loading state while fetching live chat config

      // Fetch live chat config based on the first answer
      // to determine if live chat should be displayed and with what configuration
      getSolutionContactDetailsByTag(emailTopicsMappingToBreadcrumbsAndSolutionTags[firstAnswer].solutionTag)
        .then(config => {
          setIsFetchingLiveChatConfig(false);
          setChatConfig(config);
          setDisplayLiveChat(config?.chat?.open);
        })
        .catch(() => {
          // In case of error fetching live chat config, we don't want to block the user from using the email form,
          // so we just won't display live chat and log the error
          setIsFetchingLiveChatConfig(false);
          setChatConfig(null);
          setDisplayLiveChat(false);
          console.log("Failed loading live chat for " + firstAnswer);
        });
    } else {
      setDisplayLiveChat(false);
    }
  }, [firstAnswer]);

  /**
   * TODO: Hardcoded timings for live chat hours
   *
   * This needs to be fixed to use the Contact Channel Manager
   * https://gitlab.com/JohnLewisPartnership/digital/services/customer-help/contact-channel-manager
   */
  const outOfOfficeHours = () => {
    const clientDate = new Date();
    const hours = clientDate.getUTCHours();
    const day = clientDate.getUTCDay();

    const isDuringWeek = day >= 1 && day <= 5;
    const isDuringOfficeHours = hours >= 10 && hours <= 18;

    return !isDuringWeek || !isDuringOfficeHours;
  };

  const handleQuestionAnswered = (questionId, answer) => {
    if (answer === "default") {
      return;
    }

    // Set first answer to determine live chat display and configuration if eligible
    if (questionId === "subjects") {
      setFirstAnswer(answer);
    }

    const buildNewQuestionsArr = () => {
      const result = [];
      let questionArrayComplete = false;
      let liveChatOffRampAlreadyDisplayed = false;

      questions.forEach(q => {
        if (questionArrayComplete === false) {
          if (q && emailData[q.questionId] && emailData[q.questionId].liveChat) {
            liveChatOffRampAlreadyDisplayed = true;
          }

          if (q.questionId !== questionId) {
            result.push({ ...q });
          } else {
            result.push({ questionId, answer });
            // Decision tree changed
            if (q.answer) {
              // Reset LiveChat state we have not previously run into an off-ramp in this question array
              if (!liveChatOffRampAlreadyDisplayed && liveChatDiscarded) {
                setLiveChatDiscarded(false);
              }
              onFinished(null);
            }
            questionArrayComplete = true;
          }
        }
      });
      return result;
    };

    const finish = (_questions, form) => {
      onFinished({
        formData: form || formData,
        shop: _questions.find(q => ["shop", "shopLostProperty", "returnsShop", "refundsShop"].includes(q.questionId))
          ?.answer,
      });
      router.query.t = _questions.reduce((acc, item) => `${acc}${acc ? "-" : ""}${item.answer}`, "");
      router.push(router, undefined, { shallow: true });
    };

    const newQuestions = buildNewQuestionsArr();

    // Analytics
    if (emailData[questionId]?.analyticsEvent) {
      dispatchNewEvent("dataStateChange", {
        action: emailData[questionId].analyticsEvent.action,
        type: answer,
        topic: answer,
      });
    }
    if (emailData[answer]?.form?.subject) {
      dispatchNewEvent("dataStateChange", {
        action: "email.topic",
        type: emailData[answer]?.form?.subject,
        topic: emailData[answer]?.form?.subject,
        ...emailData[answer]?.analyticsEvent,
      });
    }

    // It's last question
    if (
      !emailData[answer] ||
      (emailData[answer].form &&
        !emailData[answer].nextQuestion &&
        (!emailData[answer].liveChat ||
          liveChatDiscarded ||
          isLiveChatInEmailUsEnabled === false ||
          outOfOfficeHours() === true))
    ) {
      setQuestions(newQuestions);
      finish(newQuestions, emailData[answer]?.form);
    } else {
      // This question is not last but defines the form
      if (emailData[answer]?.form) {
        setFormData(emailData[answer].form);
      }

      setQuestions([...newQuestions, { questionId: answer }]);
    }
  };

  const renderQuestion = ({ questionId, answer }) => {
    if (
      isLiveChatInEmailUsEnabled &&
      emailData[questionId]?.liveChat &&
      !liveChatDiscarded &&
      outOfOfficeHours() === false
    ) {
      return (
        <EmailLiveChat
          questionId={questionId}
          liveChat={emailData[questionId].liveChat}
          onEmailClick={() => {
            setLiveChatDiscarded(true);
            if (!emailData[questionId].select && !emailData[questionId].nextQuestion) {
              handleQuestionAnswered(questionId, "");
            }
          }}
        />
      );
    }

    // Show live chat section only below the first question dropdown
    const displayLoadingSpinnerForLiveChat =
      chatConfig === null && isFetchingLiveChatConfig && questionId === "subjects";
    const displayLiveChatForEmailSubject = displayLiveChat && questionId === "subjects";

    const selectData = emailData[questionId]?.select || emailData[emailData[questionId]?.nextQuestion]?.select;

    if (selectData) {
      return (
        <>
          <div className={styles["email-questions__field"]}>
            {selectData.label ? <label htmlFor={questionId}>{selectData.label}</label> : null}
            <Select
              id={questionId}
              name={questionId}
              onChange={evt => handleQuestionAnswered(questionId, evt.target.value)}
              value={answer}
            >
              {selectData.options
                .map(option => ({
                  value: option.value || "default",
                  label: option.label,
                }))
                .map(({ value, label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
            </Select>
          </div>
          {displayLoadingSpinnerForLiveChat && <LoadingSpinner />}
          {displayLiveChatForEmailSubject && (
            <div className={styles["email-questions__live-chat"]}>
              <LiveChatButton
                channel={chatConfig?.chat?.channel}
                breadcrumb={emailTopicsMappingToBreadcrumbsAndSolutionTags[firstAnswer]?.breadcrumb}
                showChatIcon={true}
                startButtonText="Chat with an agent"
                liveChatInEmailUs={true}
                isFetchingNewBreadcrumb={isFetchingLiveChatConfig}
              />
            </div>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <div className={styles["email-questions"]}>
      {!hide && (
        <div className={styles["email-questions__container"]}>
          {questions.map(q => (
            <React.Fragment key={q.questionId}>{renderQuestion(q)}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export default EmailQuestions;
